class WaveClient {
  constructor() {
    this.baseUrl = 'https://gql.waveapps.com/graphql/public';
    this.token = process.env.REACT_APP_WAVE_TOKEN;
    this.useMockData = process.env.REACT_APP_USE_MOCK_DATA === 'true';
  }

  async query(query, variables = {}) {
    if (this.useMockData) {
      return this.getMockData();
    }

    try {
      const mondayToken = localStorage.getItem('monday_token');

      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.token}`,
          'Content-Type': 'application/json',
          'X-Monday-Client-Id': process.env.REACT_APP_MONDAY_CLIENT_ID,
          'X-Monday-Token': mondayToken || ''
        },
        body: JSON.stringify({ query, variables }),
      });

      if (!response.ok) {
        console.warn('Using mock data due to API error');
        return this.getMockData();
      }

      const data = await response.json();
      return data.data || this.getMockData();
    } catch (error) {
      console.error('Wave API Error:', error);
      return this.getMockData();
    }
  }

  async getBusinesses() {
    const query = `
      query {
        businesses {
          edges {
            node {
              id
              name
            }
          }
        }
      }
    `;

    const response = await this.query(query);
    // Ensure we return an array even if the response is empty
    return {
      businesses: response?.businesses?.edges?.map(edge => edge.node) || []
    };
  }

  async getInvoices(businessId) {
    const query = `
      query($businessId: ID!) {
        business(id: $businessId) {
          invoices {
            edges {
              node {
                id
                invoiceNumber
                status
                date
                dueDate
                customer {
                  name
                }
                total
              }
            }
          }
        }
      }
    `;

    const response = await this.query(query, { businessId });
    return {
      invoices: response?.business?.invoices?.edges?.map(edge => edge.node) || []
    };
  }

  async getExpenses(businessId) {
    const query = `
      query($businessId: ID!) {
        business(id: $businessId) {
          expenses {
            edges {
              node {
                id
                date
                description
                amount
                category
                status
              }
            }
          }
        }
      }
    `;

    const response = await this.query(query, { businessId });
    return {
      expenses: response?.business?.expenses?.edges?.map(edge => edge.node) || []
    };
  }

  async getProfitLossReport(filters) {
    const { businessId, startDate, endDate } = filters;
    const query = `
      query($businessId: ID!, $startDate: Date!, $endDate: Date!) {
        business(id: $businessId) {
          profitAndLoss(startDate: $startDate, endDate: $endDate) {
            revenue {
              total
              items {
                category
                amount
              }
            }
            expenses {
              total
              items {
                category
                amount
              }
            }
            netIncome
          }
        }
      }
    `;

    const response = await this.query(query, { businessId, startDate, endDate });
    return {
      business: {
        profitAndLoss: {
          revenue: {
            total: response?.business?.profitAndLoss?.revenue?.total || 0,
            items: response?.business?.profitAndLoss?.revenue?.items || []
          },
          expenses: {
            total: response?.business?.profitAndLoss?.expenses?.total || 0,
            items: response?.business?.profitAndLoss?.expenses?.items || []
          },
          netIncome: response?.business?.profitAndLoss?.netIncome || 0
        }
      }
    };
  }

  // Mock data for development
  getMockData() {
    return {
      businesses: [
        { id: '1', name: 'Test Business' }
      ],
      business: {
        invoices: {
          edges: [
            {
              node: {
                id: '1',
                invoiceNumber: 'INV-001',
                status: 'DRAFT',
                date: new Date().toISOString(),
                dueDate: new Date(Date.now() + 30*24*60*60*1000).toISOString(),
                customer: { name: 'Test Customer' },
                total: 1000.00
              }
            }
          ]
        },
        expenses: {
          edges: [
            {
              node: {
                id: '1',
                date: new Date().toISOString(),
                description: 'Test Expense',
                amount: 100.00,
                category: 'office',
                status: 'pending'
              }
            }
          ]
        },
        profitAndLoss: {
          revenue: {
            total: 1000.00,
            items: [
              { category: 'Sales', amount: 1000.00 }
            ]
          },
          expenses: {
            total: 100.00,
            items: [
              { category: 'Office Supplies', amount: 100.00 }
            ]
          },
          netIncome: 900.00
        }
      }
    };
  }
}

export default new WaveClient(); 